import { currentLoggedUserVar } from '@/shared/api/apollo/cache';
import { pathnameIncludes } from '@/shared/lib/util/location';
import { SideMenuContext } from '@/shared/model';
import { PROFILE_TRIGGER_ATTR, useSidebarStore } from '@/shared/model/SidebarStore';
import { ProfileAvatar } from '@/shared/ui/components/ProfileAvatar';
import ProfileMenu from '@/shared/ui/components/ProfileMenu';
import { SIDE_NAV_RAIL_WIDTH } from '@/shared/ui/components/SideMenus/BaseSideMenuSideNav';
import { NAV_COLORS } from '@/shared/ui/components/SideMenus/navPalette';
import { useReactiveVar } from '@apollo/client';
import BusinessIcon from '@mui/icons-material/Business';
import { alpha, IconButton, Stack, Tooltip } from '@mui/material';
import type { MouseEvent } from 'react';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * The foot of the icon rail: company and profile.
 *
 * These used to run along the bottom of the whole nav, which cost the link list its last inch of
 * height on every screen.  On the rail they cost the links nothing, and they stay reachable when
 * the list is collapsed — the rail is always there.
 */
const SidebarRailDock = () => {
  const currentUser = useReactiveVar(currentLoggedUserVar);
  const navigate = useNavigate();
  const { drawerOpen, setDrawerOpen, isPinned, setHoverExpanded } = useContext(SideMenuContext);
  const profileMenuAnchor = useSidebarStore(state => state.profileMenuAnchor);
  const setProfileMenuAnchor = useSidebarStore(state => state.setProfileMenuAnchor);

  /*
   * The avatar shows the profile popup and nothing else.  The menu and the popup used to open
   * together and stack, so opening the popup now closes the menu.  A pinned menu the popup closed
   * comes back when the popup goes -- the user chose to keep it open, and only borrowed the space.
   */
  const reopenPinnedMenu = useRef(false);
  const openProfileMenu = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (drawerOpen) {
        if (isPinned) {
          reopenPinnedMenu.current = true;
          setDrawerOpen(false);
        } else {
          setHoverExpanded(false);
        }
      }
      setProfileMenuAnchor(event.currentTarget);
    },
    [drawerOpen, isPinned, setDrawerOpen, setHoverExpanded, setProfileMenuAnchor]
  );

  useEffect(() => {
    if (!profileMenuAnchor && reopenPinnedMenu.current) {
      reopenPinnedMenu.current = false;
      if (useSidebarStore.getState().isPinned) setDrawerOpen(true);
    }
  }, [profileMenuAnchor, setDrawerOpen]);

  // Clear the anchor on unmount, or the next dock opens the popup against a detached avatar (top-left).
  useEffect(() => () => useSidebarStore.getState().setProfileMenuAnchor(null), []);

  const companyName = currentUser?.relyingParty?.name ?? 'My Pharmacy';
  // The same destination as Profile > My Company, so the two never disagree.
  const companyPath = `/administration/system/companies/${currentUser?.relyingParty?.id}`;

  // Same inversion as the rail above it: active means white ground, logo ink.
  const dockButtonSx = (active = false) => ({
    color: active ? NAV_COLORS.inkIcon : 'inherit',
    borderRadius: 1,
    opacity: active ? 1 : 0.8,
    ...(active && { backgroundColor: NAV_COLORS.onDark }),
    '&:hover': {
      opacity: 1,
      backgroundColor: active ? NAV_COLORS.onDark : alpha(NAV_COLORS.onDark, 0.14)
    }
  });

  return (
    <Stack
      spacing={1.25}
      sx={{
        alignItems: 'center',
        width: SIDE_NAV_RAIL_WIDTH,
        flexShrink: 0,
        paddingY: 1,
        borderTop: `1px solid ${alpha(NAV_COLORS.onDark, 0.15)}`,
        backgroundColor: NAV_COLORS.rail,
        color: NAV_COLORS.onDark
      }}
    >
      <Tooltip title={companyName} placement="right">
        <IconButton
          onClick={() => navigate(companyPath)}
          size="small"
          sx={dockButtonSx(pathnameIncludes('/administration/system/companies'))}
        >
          <BusinessIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <ProfileMenu
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        anchorEl={profileMenuAnchor}
        onAnchorElChange={setProfileMenuAnchor}
        nonBlocking
        renderTrigger={({ name, avatarUrl, roleTypeString }) => (
          <Tooltip title={`${name} · ${roleTypeString}`} placement="right">
            {/*
              Reaching sign-out or switch-company meant a click on a 32px target in the very
              corner of the screen; hovering it opens the same menu.  It closes on a click outside
              it, or when the pointer moves on to the rest of the nav.
            */}
            <IconButton
              {...{ [PROFILE_TRIGGER_ATTR]: true }}
              onClick={openProfileMenu}
              onMouseEnter={openProfileMenu}
              size="small"
              sx={{ padding: 0.5, marginTop: 0.25 }}
            >
              <ProfileAvatar avatarUrl={avatarUrl} name={name} sx={{ width: 32, height: 32 }} />
            </IconButton>
          </Tooltip>
        )}
      />
    </Stack>
  );
};

export default SidebarRailDock;
